import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit'
import { AppState } from '@redux/store'
import {
  ClaimRecordStatus,
  ClaimRecord,
  ClaimRecordLifeCycle,
  CustomerFacingClaimRecordStatus,
  ActiveClaimCheckStatus,
  NullifyArgs,
} from '@typesApp/ClaimRecord'
import { InsuranceUsage } from '@typesApp/ClaimData'
import { ClaimDetails } from '@typesApp/ClaimDetails'
import { QuoteStatus, QuoteStep, QuoteErrorData } from '@typesApp/QuoteData'
import { QuoteDetails } from '@typesApp/QuoteDetails'
import { EligibleProductStatus } from '@typesApp/Product'
import { Price } from '@typesApp/Price'
import {
  allowStartClaim,
  getClaimDetails,
  isNullifiedStatus,
  isRestrictedStatus,
  isSystemRefusedEventSentStatus,
  isForManualNullify,
  getEligibleProductStatus,
} from '@utils/claimUtil'
import { isProductInsuranceEligible } from '@utils/isProductInsuranceEligible'
import { getQuoteDetails, getQuoteStep } from '@utils/quoteUtil'
import { claimRecordsApi } from '@redux/queries/claimRecords'
import { authApi } from '@redux/queries/auth'
import { connectorSlice } from './connectorSlice'

export type ClaimStatusState = {
  activeClaimCheckStatus: ActiveClaimCheckStatus
  eligibleProductStatus: EligibleProductStatus
  allowStartClaim: boolean
  claimDetails: ClaimDetails
  quoteDetails: QuoteDetails
  quoteStep?: QuoteStep
  initial: boolean
  isOrderComplete: boolean
  isRestricted: boolean
  isRestrictedCheckPerformed?: boolean
  isInitiateClaimError?: boolean
  isSkipActiveDataCheck?: boolean
  claimRecordDeleted?: boolean
  claimRecordFetched?: boolean
}

const initialClaimDetails = {
  claimRecordStatus: ClaimRecordStatus.NONE,
  customerFacingStatus: CustomerFacingClaimRecordStatus.NO_CLAIM_RECORD,
} as ClaimDetails

const initialQuoteDetails = {
  status: QuoteStatus.NONE,
} as QuoteDetails

const initialState: ClaimStatusState = {
  activeClaimCheckStatus: ActiveClaimCheckStatus.NONE,
  eligibleProductStatus: EligibleProductStatus.NA,
  allowStartClaim: false,
  claimDetails: initialClaimDetails,
  quoteDetails: initialQuoteDetails,
  initial: true,
  isOrderComplete: false,
  isRestricted: false,
}

export const claimStatusSlice = createSlice({
  name: 'claimStatus',
  initialState: initialState,
  reducers: {
    setAllowStartClaim(state, action: PayloadAction<boolean>) {
      state.allowStartClaim = action.payload
    },
    /**
     * Reset some state on drawer close
     */
    resetClaimStatus(state) {
      state.initial = true
      state.quoteStep = undefined
      state.isInitiateClaimError = undefined
    },
    /**
     * Initialize quote step on drawer open
     */
    initClaimStatus(state, action: PayloadAction<ClaimRecord | undefined>) {
      state.initial = false
      state.quoteStep = action.payload && getQuoteStep({ claimRecord: action.payload, quoteRequested: false })
    },
    setQuoteStep(state, action: PayloadAction<QuoteStep>) {
      state.quoteStep = action.payload
    },
    setInitiateClaimError(state, action: PayloadAction<boolean>) {
      state.isInitiateClaimError = action.payload
    },
    /**
     * Force limit reached CTA state
     */
    claimLimitReached(state) {
      state.claimDetails.customerFacingStatus = CustomerFacingClaimRecordStatus.LIMIT_REACHED
    },
    setEmptyEligibleProducts(state) {
      state.eligibleProductStatus = EligibleProductStatus.NOT_ELIGIBLE
      state.allowStartClaim = allowStartClaim(state.claimDetails?.customerFacingStatus)
    },
    setSkipActiveDataCheck(state, action: PayloadAction<boolean>) {
      state.isSkipActiveDataCheck = action.payload
      if (action.payload) {
        state.activeClaimCheckStatus = ActiveClaimCheckStatus.COMPLETE
      }
    },
    setActiveClaimRecord(state, action: PayloadAction<string | undefined>) {
      state.claimDetails = {
        ...state.claimDetails,
        claimRecordId: action.payload,
      }
      state.activeClaimCheckStatus = ActiveClaimCheckStatus.COMPLETE
      if (!action.payload) {
        state.claimRecordFetched = true
      }
    },
    setClaimRecordDeleted(state) {
      state.claimDetails = initialClaimDetails
      state.quoteDetails = initialQuoteDetails
      state.allowStartClaim = true
      state.claimRecordDeleted = true
    },
    initEligibleProductCheck(state) {
      if (state.eligibleProductStatus === EligibleProductStatus.NA) {
        state.eligibleProductStatus = EligibleProductStatus.PENDING
      }
    },
    resetClaimStatusInitialState() {
      return initialState
    },
  },
  extraReducers: builder => {
    builder.addCase(connectorSlice.actions.connectorInitAnonymousSession, state => {
      state.activeClaimCheckStatus = ActiveClaimCheckStatus.NONE
      state.eligibleProductStatus = EligibleProductStatus.NA
    })

    /**
     * Order complete notification
     */
    builder.addCase(connectorSlice.actions.connectorOrderComplete, state => {
      state.isOrderComplete = true
      state.claimDetails = initialClaimDetails
      state.quoteDetails = initialQuoteDetails
    })

    /**
     * Active claim check status
     */
    builder.addMatcher(
      isAnyOf(authApi.endpoints.getAuthToken.matchPending, claimRecordsApi.endpoints.getActiveClaimRecord.matchPending),
      (state, action) => {
        if (state.activeClaimCheckStatus === ActiveClaimCheckStatus.NONE) {
          state.activeClaimCheckStatus = ActiveClaimCheckStatus.PENDING
        }
      }
    )

    builder.addMatcher(
      isAnyOf(
        authApi.endpoints.getAuthToken.matchRejected,
        claimRecordsApi.endpoints.getActiveClaimRecord.matchRejected
      ),
      (state, action) => {
        state.activeClaimCheckStatus = ActiveClaimCheckStatus.COMPLETE
      }
    )

    /**
     * Update the claim details when claim record is fetched
     */
    builder.addMatcher(claimRecordsApi.endpoints.getClaimRecord.matchFulfilled, (state, action) => {
      state.claimRecordFetched = true

      // claim record also reflects the restricted status
      state.isRestrictedCheckPerformed = true
      if (state.isRestricted || isSystemRefusedEventSentStatus(action.payload)) {
        return
      }

      // reset to initial state if order is complete
      if (state.isOrderComplete) {
        state.claimDetails = initialClaimDetails
        state.quoteDetails = initialQuoteDetails
        return
      }

      if (isForManualNullify(action.payload.status)) {
        state.claimDetails = initialClaimDetails
        state.quoteDetails = initialQuoteDetails
      } else {
        state.claimDetails = getClaimDetails(action.payload) || initialClaimDetails
        state.quoteDetails = getQuoteDetails(action.payload) || initialQuoteDetails
      }

      state.allowStartClaim = allowStartClaim(state.claimDetails.customerFacingStatus)
      state.isRestricted = isRestrictedStatus(action.payload?.status)

      // NOTE: some brands (e.g. VDE) have insurer specific eligibility restrictions so
      // the products in the claim record may NOT be eligible (due to the insurer) but the cart
      // products may be eligible.

      // If the claim record indicates NOT_ELIGIBLE, fallback to the initial status
      // which is determined by the check-insurance-eligiblity API in useEligibleProductCheck hook.
      const eligibleProductStatus = getEligibleProductStatus(action.payload?.eligibleProducts ?? [])
      if (eligibleProductStatus !== EligibleProductStatus.NOT_ELIGIBLE) {
        state.eligibleProductStatus = eligibleProductStatus
      }
    })

    /**
     * Refresh claim record to ensure CTA shows correct state immediately
     */
    builder.addMatcher(
      isAnyOf(
        claimRecordsApi.endpoints.getQuote.matchFulfilled,
        claimRecordsApi.endpoints.refuseQuote.matchFulfilled,
        claimRecordsApi.endpoints.acceptQuote.matchFulfilled,
        claimRecordsApi.endpoints.nullifyClaim.matchFulfilled,
        claimRecordsApi.endpoints.optInRetryQuote.matchFulfilled,
        claimRecordsApi.endpoints.cancelOptInRetryQuote.matchFulfilled
      ),
      (state, action) => {
        // avoid updating claim record details if nullify last attempt
        // to prevent UI from re-rendering
        const originalArgs = action.meta.arg.originalArgs
        const isLastNullifyAttempt = Boolean((originalArgs as NullifyArgs)?.isLastAttempt)
        if (isLastNullifyAttempt) {
          return
        }

        const claimRecord = action.payload?.claimRecord
        if (isNullifiedStatus(claimRecord?.status)) {
          state.claimDetails = initialClaimDetails
          state.quoteDetails = initialQuoteDetails
        } else {
          state.claimDetails = getClaimDetails(claimRecord) ?? initialClaimDetails
          state.quoteDetails = getQuoteDetails(claimRecord) ?? initialQuoteDetails
        }

        state.allowStartClaim = allowStartClaim(state.claimDetails.customerFacingStatus)
        state.isRestricted = isRestrictedStatus(claimRecord.status)
      }
    )

    builder.addMatcher(claimRecordsApi.endpoints.getClaimRecord.matchRejected, (state, action) => {
      const status = action.payload?.status
      if (status === 404) {
        state.claimRecordDeleted = true
        state.claimDetails = initialClaimDetails
        state.quoteDetails = initialQuoteDetails
        state.allowStartClaim = true
      }
      state.claimRecordFetched = true
    })

    /**
     * On create claim record clear the order complete flag so next fetch will update claim details
     */
    builder.addMatcher(claimRecordsApi.endpoints.createClaimRecord.matchFulfilled, (state, action) => {
      state.isOrderComplete = false
      state.claimRecordDeleted = undefined
      state.isSkipActiveDataCheck = undefined
      state.claimDetails = getClaimDetails(action.payload) || initialClaimDetails
    })

    builder.addMatcher(claimRecordsApi.endpoints.createClaimRecord.matchRejected, (state, action) => {
      state.isInitiateClaimError = true
      state.isSkipActiveDataCheck = undefined
    })

    /**
     * Remove claim details after claim record deleted
     */
    builder.addMatcher(claimRecordsApi.endpoints.deleteClaimRecord.matchFulfilled, state => {
      state.claimDetails = initialClaimDetails
      state.allowStartClaim = true
      state.claimRecordDeleted = true
    })

    /**
     * Restricted status
     */
    builder.addMatcher(claimRecordsApi.endpoints.restrictedCheck.matchFulfilled, (state, action) => {
      state.isRestrictedCheckPerformed = true
      state.isRestricted = action.payload.restricted
      if (state.isRestricted) {
        state.allowStartClaim = false
        state.claimDetails.claimRecordStatus = ClaimRecordStatus.RESTRICTED
        state.claimDetails.customerFacingStatus = CustomerFacingClaimRecordStatus.LIMIT_REACHED
      }
    })

    builder.addMatcher(claimRecordsApi.endpoints.restrictedCheck.matchRejected, (state, action) => {
      state.isRestrictedCheckPerformed = true
    })

    /**
     * Update eligible product status and cache after eligible product check
     */
    builder.addMatcher(claimRecordsApi.endpoints.checkEligibleProducts.matchFulfilled, (state, action) => {
      const cartProducts = action.payload.eligibleProducts || []
      const eligibleProducts = cartProducts.filter(isProductInsuranceEligible)
      state.eligibleProductStatus =
        eligibleProducts.length > 0 ? EligibleProductStatus.ELIGIBLE : EligibleProductStatus.NOT_ELIGIBLE
      state.allowStartClaim =
        !state.isRestricted && allowStartClaim(state.claimDetails?.customerFacingStatus) && eligibleProducts.length > 0
    })

    /**
     * To allow cta to render in case product eligibility check fails
     */
    builder.addMatcher(claimRecordsApi.endpoints.checkEligibleProducts.matchRejected, (state, action) => {
      if (state.eligibleProductStatus === EligibleProductStatus.PENDING) {
        state.eligibleProductStatus = EligibleProductStatus.NA
      }
    })

    /**
     * Reset to initial state on session expiry or logout
     */
    builder.addMatcher(
      isAnyOf(connectorSlice.actions.sessionExpired, connectorSlice.actions.connectorLogoutInProgress),
      () => {
        return initialState
      }
    )
  },
})

export const {
  setAllowStartClaim,
  resetClaimStatus,
  initClaimStatus,
  claimLimitReached,
  setQuoteStep,
  setInitiateClaimError,
  setEmptyEligibleProducts,
  setSkipActiveDataCheck,
  setActiveClaimRecord,
  setClaimRecordDeleted,
  initEligibleProductCheck,
  resetClaimStatusInitialState,
} = claimStatusSlice.actions

export const claimDetailsSelector = (state: AppState): ClaimDetails | undefined => state.claimStatus.claimDetails
export const claimRecordIdSelector = (state: AppState): string => state.claimStatus.claimDetails?.claimRecordId || ''
export const claimRecordInsurerNameSelector = (state: AppState): string =>
  state.claimStatus.claimDetails?.insurerName ?? ''
export const claimRecordNormalizedLocaleSelector = (state: AppState): string =>
  state.claimStatus.claimDetails?.normalizedLocale || ''
export const claimRecordStatusSelector = (state: AppState): ClaimRecordStatus =>
  state.claimStatus.claimDetails?.claimRecordStatus || ClaimRecordStatus.NONE
export const claimRecordInsuranceUsageSelector = (state: AppState): InsuranceUsage | undefined =>
  state.claimStatus.claimDetails?.useInsurance
export const claimRecordFetchedSelector = (state: AppState): boolean => state.claimStatus.claimRecordFetched === true
export const claimRecordDeletedSelector = (state: AppState): boolean => state.claimStatus.claimRecordDeleted === true
export const claimRecordLifeCycleSelector = (state: AppState): ClaimRecordLifeCycle =>
  state.claimStatus.claimDetails.lifeCycle || ClaimRecordLifeCycle.SESSION_ONLY
export const claimRecordOrderSubtotalSelector = (state: AppState): Price | undefined =>
  state.claimStatus.claimDetails.orderSubtotal
export const quoteStatusSelector = (state: AppState): QuoteStatus => state.claimStatus.quoteDetails.status
export const quoteErrorSelector = (state: AppState): QuoteErrorData | undefined => state.claimStatus.quoteDetails.error
export const quoteStepSelector = (state: AppState): QuoteStep | undefined => state.claimStatus.quoteStep
export const quoteDetailsSelector = (state: AppState): QuoteDetails | undefined => state.claimStatus.quoteDetails
export const customerFacingClaimRecordStatusSelector = (state: AppState): CustomerFacingClaimRecordStatus =>
  state.claimStatus.claimDetails?.customerFacingStatus || CustomerFacingClaimRecordStatus.NO_CLAIM_RECORD
export const allowStartClaimSelector = (state: AppState): boolean => state.claimStatus.allowStartClaim
export const initiateClaimErrorSelector = (state: AppState): boolean | undefined =>
  state.claimStatus.isInitiateClaimError
export const eligibleProductStatusSelector = (state: AppState): EligibleProductStatus =>
  state.claimStatus.eligibleProductStatus
export const activeClaimPerformedSelector = (state: AppState): boolean =>
  state.claimStatus.activeClaimCheckStatus === ActiveClaimCheckStatus.COMPLETE
export const activeClaimCheckStatusSelector = (state: AppState): ActiveClaimCheckStatus =>
  state.claimStatus.activeClaimCheckStatus
export const skipActiveDataCheckSelector = (state: AppState): boolean =>
  state.claimStatus.isSkipActiveDataCheck === true
export const restrictedStatusSelector = (state: AppState): boolean => state.claimStatus.isRestricted
export const restrictedCheckPerformedSelector = (state: AppState): boolean =>
  state.claimStatus.isRestrictedCheckPerformed === true
